import dayjs from 'dayjs';
import JSZip from 'jszip';

const BULK_EXPORT_FILE_TYPES = [
  {
    key: 'includeCv',
    field: 'cv_path',
    labelKey: 'Životopis',
    targetName: 'zivotopis',
  },
  {
    key: 'includeMotivationLetter',
    field: 'motivation_letter_path',
    labelKey: 'Motivačný list',
    targetName: 'motivacny_list',
  },
  {
    key: 'includePhoto',
    field: 'photo_path',
    labelKey: 'Fotografia',
    targetName: 'fotografia',
  },
  {
    key: 'includeLanguageCertificate',
    field: 'language_certificate_path',
    labelKey: 'Jazykový certifikát',
    targetName: 'jazykovy_certifikat',
  },
];

export const createBulkExportRuntime = () => ({
  tasks: [],
  currentIndex: 0,
  completed: 0,
  entries: [],
  errors: [],
  skipAllErrors: false,
});

export const buildExportFileName = (application) => {
  const base = `${application?.person?.last_name || 'Nezname'}_${
    application?.person?.first_name || 'Pouzivatel'
  }`;

  return base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
    .replace(/\s+/g, '_');
};

const getExtension = (filename) => {
  const name = String(filename || '');
  const index = name.lastIndexOf('.');

  if (index < 0 || index === name.length - 1) {
    return '';
  }

  return name.slice(index + 1);
};

const base64ToUint8Array = (base64) => {
  const normalized = String(base64 || '');
  const byteCharacters = atob(normalized);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  return new Uint8Array(byteNumbers);
};

const blobToUint8Array = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

export const createBulkExportTasks = ({ applications, options, t }) => {
  const tasks = [];
  const folderNameCounts = new Map();

  applications.forEach((application) => {
    const baseFolderName = buildExportFileName(application);
    const currentCount = folderNameCounts.get(baseFolderName) || 0;
    const folderName =
      currentCount === 0
        ? baseFolderName
        : `${baseFolderName} (${currentCount})`;

    folderNameCounts.set(baseFolderName, currentCount + 1);

    BULK_EXPORT_FILE_TYPES.forEach((fileType) => {
      if (!options[fileType.key]) {
        return;
      }

      tasks.push({
        type: 'file',
        application,
        folderName,
        fileType: {
          ...fileType,
          label: t(fileType.labelKey),
        },
        label: `${folderName}: ${t(fileType.labelKey)}`,
      });
    });

    if (options.includePdf) {
      tasks.push({
        type: 'pdf',
        application,
        folderName,
        label: `${folderName}: PDF`,
      });
    }
  });

  return tasks;
};

export const executeBulkExportTask = async ({
  task,
  downloadPrivateFile,
  generateApplicationPDF,
  t,
}) => {
  if (task.type === 'file') {
    const { application, folderName, fileType } = task;
    const sourceFilename = application?.[fileType.field];

    if (!sourceFilename) {
      return {
        ok: false,
        error: `${folderName}: ${fileType.label} - ${t('nepodarilo sa načítať')}`,
      };
    }

    try {
      const fileResult = await downloadPrivateFile(
        { id: application.id, filename: sourceFilename },
        true,
      );

      if (fileResult?.error || !fileResult?.data?.pdf) {
        return {
          ok: false,
          error: `${folderName}: ${fileType.label} - ${t('nepodarilo sa načítať')}`,
        };
      }

      const fileExt = getExtension(sourceFilename);
      const targetFile = fileExt
        ? `${fileType.targetName}.${fileExt}`
        : fileType.targetName;

      return {
        ok: true,
        entry: {
          path: `${folderName}/${targetFile}`,
          data: base64ToUint8Array(fileResult.data.pdf),
        },
      };
    } catch {
      return {
        ok: false,
        error: `${folderName}: ${fileType.label} - ${t('nepodarilo sa načítať')}`,
      };
    }
  }

  if (task.type === 'pdf') {
    const { application, folderName } = task;

    try {
      const pdfResponse = await generateApplicationPDF(application).unwrap();
      let pdfBytes = null;

      if (pdfResponse?.pdf) {
        pdfBytes = base64ToUint8Array(pdfResponse.pdf);
      } else if (pdfResponse instanceof Blob) {
        pdfBytes = await blobToUint8Array(pdfResponse);
      }

      if (!pdfBytes) {
        return {
          ok: false,
          error: `${folderName}: PDF - ${t('nepodarilo sa načítať')}`,
        };
      }

      return {
        ok: true,
        entry: {
          path: `${folderName}/prihlaska.pdf`,
          data: pdfBytes,
        },
      };
    } catch {
      return {
        ok: false,
        error: `${folderName}: PDF - ${t('nepodarilo sa načítať')}`,
      };
    }
  }

  return { ok: false, error: t('Neznámy typ exportu') };
};

export const downloadBulkExportZip = async (entries) => {
  const zip = new JSZip();

  entries.forEach((entry) => {
    zip.file(entry.path, entry.data);
  });

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'STORE',
  });
  const url = window.URL.createObjectURL(zipBlob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = `Erasmus_Export_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

export const processBulkExportQueue = async ({
  runtime,
  executeTask,
  t,
  onBeforeStart,
  onProgress,
  onPaused,
  onCompleted,
  onEmpty,
}) => {
  const total = runtime.tasks.length;

  if (total === 0) {
    onEmpty({
      completed: 0,
      total: 0,
      current: t('Export neobsahuje žiadne súbory'),
      errors: [],
    });
    return;
  }

  const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

  onBeforeStart();

  while (runtime.currentIndex < runtime.tasks.length) {
    const task = runtime.tasks[runtime.currentIndex];

    onProgress({
      completed: runtime.completed,
      total,
      current: task.label,
      errors: [...runtime.errors],
    });

    const result = await executeTask(task);
    runtime.currentIndex += 1;
    runtime.completed += 1;

    if (result.ok && result.entry) {
      runtime.entries.push(result.entry);
    } else if (!result.ok) {
      runtime.errors.push(result.error);

      if (!runtime.skipAllErrors) {
        onPaused({
          completed: runtime.completed,
          total,
          current: t('Export prerušený'),
          errors: [...runtime.errors],
        });
        return;
      }
    }

    onProgress({
      completed: runtime.completed,
      total,
      current: task.label,
      errors: [...runtime.errors],
    });

    await nextTick();
  }

  if (runtime.entries.length === 0) {
    onEmpty({
      completed: runtime.completed,
      total,
      current: t('Export neobsahuje žiadne súbory'),
      errors: [...runtime.errors],
    });
    return;
  }

  await downloadBulkExportZip(runtime.entries);

  onCompleted({
    completed: runtime.completed,
    total,
    current: t('Export dokončený'),
    errors: [...runtime.errors],
  });
};
